import { EventAggregator } from 'aurelia-event-aggregator';
import { bindable } from 'aurelia-framework';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ValidationController, ValidationControllerFactory, ValidationRules, Validator } from 'aurelia-validation';
import { NettingTypeService } from 'models';
import { Models } from 'models/core';
import { NettingTypeServiceService } from 'services';
import { ErrorService } from 'services/error-service';
import { MeshSizeService } from 'services/mesh-size-service';
import { NylonTypeService } from 'services/nylon-type-service';
import { ServiceConsumptionThreadService } from 'services/service-consumption-thread-service';
import { ThreadTypeService } from 'services/thread-type-service';
import { ToastService } from 'services/toast-service';
import { ServiceProductService } from 'services/service-product-service';
import { LocationCodeService } from 'services/location-code-service';

@autoinject
export class ThreadInlineNew {
  @bindable private service: Models.Service;
  @bindable private newFormVisible;

  private thread: Models.ServiceConsumptionThread = new Models.ServiceConsumptionThread();
  private currentThreadTypeId: number;
  private currentNylonTypeId: number;
  private currentMeshSizeId: number;

  private nettings: NettingTypeService[] = [];
  protected selectedNetting: NettingTypeService;
  private validationController: ValidationController;

  constructor(
    private errorService: ErrorService,
    private eventAggregator: EventAggregator,
    private toastService: ToastService,
    private serviceConsumptionThreadService: ServiceConsumptionThreadService,
    protected threadTypeService: ThreadTypeService,
    protected nylonTypeService: NylonTypeService,
    protected meshSizeService: MeshSizeService,
    protected nettingTypesServiceService: NettingTypeServiceService,
    private validator: Validator,
    private t: I18N,
    validationControllerFactory: ValidationControllerFactory,
    private serviceProductservice: ServiceProductService,
    private locationCodeService: LocationCodeService  
  ) {
    this.validationController = validationControllerFactory.createForCurrentScope();
  }

  protected applyValidationRules(item: Models.ServiceConsumptionThread) {
    if (this.service.IsInvoicingCompatible) {
      ValidationRules.ensure('NettingTypeServiceId')
        .required()
        .withMessage(this.t.tr('general.requiredField'))
        .ensure('Consumption')
        .required()
        .withMessage(this.t.tr('general.requiredField'))
        .min(0)
        .withMessage(this.t.tr('validation.mustBeGreaterOrEqualToValue', { value: 0 }))
        .ensure('ProductionNumber')
        .required()
        .withMessage(this.t.tr('general.requiredField'))
        .on(item);
    } else {
      ValidationRules.ensure('ProductionNumber').required().withMessage(this.t.tr('general.requiredField')).on(item);
    }
  }

  private async validate(item: Models.ServiceConsumptionThread) {
    this.applyValidationRules(item);
    await this.validator.validateObject(item);
    return (await this.validationController.validate({ object: item })).valid;
  }

  protected attached() {
    this.applyDefaultLocationCode();
    void this.getNettings();
  }

  private applyDefaultLocationCode() {
    const defaultLocationCodeId = this.service?.ServiceStation?.DefaultLocationCodeId;
    if (defaultLocationCodeId && !this.thread.LocationCodeId) {
      this.thread.LocationCodeId = defaultLocationCodeId;
    }
  }

  private async getNettings() {
    const result = await this.serviceProductservice.getNettingTypesService('Repair', this.service.Id);
    this.nettings = result.data;
  }

  protected nettingTypeChanged({ detail }: { detail: { value: string } }) {
    const nettingTypeId = +detail.value;
    const netting = this.nettings.find((n) => n.Id === nettingTypeId);
    if (!netting) return;

    this.selectedNetting = netting;
    this.thread.MeshSizeId = netting.MeshSizeId || null;
    this.thread.NylonTypeId = netting.NylonTypeId || null;
    this.thread.ThreadTypeId = netting.ThreadTypeId || null;
    this.thread.NettingTypeServiceId = nettingTypeId;
  }

  protected async createThread() {
    try {
      // remove expanded objects
      delete this.thread.ThreadType;
      delete this.thread.NylonType;
      delete this.thread.MeshSize;
      delete this.thread.NettingTypeService;
      delete this.thread.LocationCode;

      if (!this.service.IsInvoicingCompatible) {
        this.thread.ThreadTypeId = this.currentThreadTypeId;
        this.thread.NylonTypeId = this.currentNylonTypeId;
        this.thread.MeshSizeId = this.currentMeshSizeId;
      }
      if (!(await this.validate(this.thread))) return;
      this.thread.LocationCodeId = this.thread.LocationCodeId || null;

      // add serviceId
      this.thread.ServiceId = this.service.Id;

      await this.serviceConsumptionThreadService.post(this.thread);
      this.thread = null;
      this.eventAggregator.publish('serviceConsumptionThreadsListReset', 0);
      this.toastService.showSuccess('service.consumptionThreadCreated');
      this.newFormVisible = false;
    } catch (err) {
      this.errorService.handleError(err);
    }
  }

  protected cancel() {
    this.newFormVisible = null;
    this.eventAggregator.publish('serviceConsumptionThreadsListCancel', 0);
  }

  protected threadTypeChanged(event) {
    this.currentThreadTypeId = event.detail.value;
  }

  protected nylonTypeChanged(event) {
    this.currentNylonTypeId = event.detail.value;
  }

  protected meshSizeChanged(event) {
    this.currentMeshSizeId = event.detail.value;
  }
}
